import React, { ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

type Theme = "light" | "dark";

interface ThemeProviderProps {
    children: ReactNode;
    attribute?: "class" | "data-theme"; // mimic next-themes API
    value?: { light?: string; dark?: string }; // CSS class names
    defaultTheme?: Theme;
    enableSystem?: boolean;
}

interface ThemeContextValue {
    theme: Theme;
    setTheme: (theme: Theme) => void;
    toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
    children,
    attribute = "class",
    value = { light: "light", dark: "dark" },
    defaultTheme = "light",
    enableSystem = false,
}) => {
    const getSystemTheme = (): Theme => {
        if (!enableSystem) return defaultTheme;
        return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
    };

    const [theme, setThemeState] = useState<Theme>(() => {
        const stored = localStorage.getItem("theme") as Theme | null;
        return stored || getSystemTheme() || defaultTheme;
    });

    const setTheme = useCallback((value: Theme) => {
        setThemeState(value);
        localStorage.setItem("theme", value);
    }, []);

    const toggleTheme = useCallback(() => {
        setThemeState((prev) => {
            const nextTheme = prev === "light" ? "dark" : "light";
            localStorage.setItem("theme", nextTheme);
            return nextTheme;
        });
    }, []);

    const contextValue = useMemo<ThemeContextValue>(() => ({ theme, setTheme, toggleTheme }), [theme, setTheme, toggleTheme]);

    useEffect(() => {
        const element = document.documentElement;
        const lightClass = value.light ?? "light";
        const darkClass = value.dark ?? "dark";

        if (attribute === "class") {
            element.classList.remove(lightClass, darkClass);
            element.classList.add(theme === "light" ? lightClass : darkClass);
        } else if (attribute === "data-theme") {
            element.setAttribute("data-theme", theme);
        }
    }, [theme, attribute, value.light, value.dark]);

    return <ThemeContext.Provider value={contextValue}>{children}</ThemeContext.Provider>;
};

export function useTheme(): ThemeContextValue {
    const ctx = useContext(ThemeContext);
    if (!ctx) throw new Error("useTheme must be used inside ThemeProvider");
    return ctx;
}
